import { useEffect, useState } from "react";
import {
    AlertTriangle,
    ArrowDownRight,
    ArrowUpRight,
    BarChart3,
    Bell,
    Info,
    Minus,
    Shield,
    TrendingDown,
    TrendingUp,
    Zap,
} from "lucide-react";
import { InteractiveAreaChart } from "./statusHelpers";
import { myLocalStorage } from "../../StorageHelper";
import { NGROK } from "../../../APIs";
import axios from "axios";
import "./AlertTrendPanel.css";

// ─── Types ──────────────────────────────────────────────

interface DayBucket {
    label: string;
    alertCount: number;
}

/** Pre-computed payload returned by GET /api/cloud/alert-trend */
interface AlertTrend {
    hasData: boolean;
    trend: "improving" | "worsening" | "stable";
    buckets: DayBucket[];
    latest: number;
    previous: number;
    oldest: number;
    delta: number;
    overallDelta: number;
    maxCount: number;
    minCount: number;
    avgCount: number;
    peakLabel: string;
    lowLabel: string;
    maxJump: number;
    maxJumpDay: string;
    volatilityPct: number;
}

interface AlertTrendPanelProps {
    cloudProvider: string;
    /** Which entities' alerts to trend: USER (human users), SERVICE_ACCOUNT (NHI / IAM roles / Azure identities), or ALL. */
    scope?: "USER" | "SERVICE_ACCOUNT" | "ALL";
    onCompare?: () => void;
}

// ─── Helpers ────────────────────────────────────────────

/** Map a backend bucket label to the chart's x-axis label ("Today" -> "TODAY", "6d ago" -> "6D"). */
function toChartLabel(label: string): string {
    return label === "Today" ? "TODAY" : label.split(" ")[0].toUpperCase();
}

// ─── Component ──────────────────────────────────────────

export default function AlertTrendPanel({ cloudProvider, scope = "ALL" }: AlertTrendPanelProps) {
    const [data, setData] = useState<AlertTrend | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const tenantName =
            myLocalStorage.getItem("latestTenant")?.tenantName ||
            myLocalStorage.getItem("latestTenant");

        const cp = cloudProvider?.toLowerCase();
        let cloudId: string | null = null;
        if (cp === "gcp") {
            cloudId = myLocalStorage.getItem("gcpSelectedProjectId");
        } else if (cp === "aws") {
            cloudId = myLocalStorage.getItem("selectedAwsAccount");
        } else if (cp === "azure") {
            const sub = myLocalStorage.getItem("latestSubList");
            cloudId = sub?.azureSubscriptionId || null;
        }

        if (!tenantName || !cloudId) {
            setLoading(false);
            return;
        }

        const fetchAlertTrend = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${NGROK}/api/cloud/alert-trend`, {
                    params: {
                        tenantName,
                        cloudId,
                        cloudType: cloudProvider?.toUpperCase(),
                        scope,
                    },
                });
                setData(response.data as AlertTrend);
            } catch {
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchAlertTrend();
    }, [cloudProvider, scope]);

    // ── Loading state ───────────────────────────────────
    if (loading) {
        return (
            <div className="snapshot-comparison">
                <div className="sc-insights" style={{ flex: 1 }}>
                    <div className="sc-insights-header">
                        <AlertTriangle className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                        <span className="sc-insights-title">Alert Trend Analysis · Last 7 Days</span>
                    </div>
                    <div className="sc-insight-items">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="h-4 bg-muted/50 animate-pulse rounded-md mb-2" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const buckets = data?.buckets ?? [];
    const chartValues = buckets.map((b) => b.alertCount);
    const chartLabels = buckets.map((b) => toChartLabel(b.label));

    // ── No data state ───────────────────────────────────
    // When there are zero alerts / no trend data, hide the card entirely.
    if (!data || !data.hasData) {
        return null;
    }

    // ── Full data view (all figures pre-computed by the backend) ──
    const { trend, latest, delta, overallDelta, maxCount, minCount, avgCount,
        peakLabel, lowLabel, maxJump, maxJumpDay, volatilityPct } = data;

    return (
        <div className="snapshot-comparison">
            {/* Left: Insights */}
            <div className="sc-insights">
                <div className="sc-insights-header">
                    <AlertTriangle className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                    <span className="sc-insights-title">Alert Trend Analysis · Last 7 Days</span>
                    <div className={`sc-trend-badge ${trend}`}>
                        {trend === "improving" ? (
                            <><TrendingDown /> Improving</>
                        ) : trend === "worsening" ? (
                            <><TrendingUp /> Worsening</>
                        ) : (
                            <><Minus /> Stable</>
                        )}
                    </div>
                </div>

                <div className="sc-insight-items">
                    <div className="sc-insight-item">
                        <Bell />
                        <span>
                            Today's snapshot has <strong>{latest} alerts</strong>
                            {delta !== 0 && (
                                <>
                                    {" "}
                                    <span className={`sc-delta ${delta > 0 ? "positive" : "negative"}`}>
                                        {delta > 0 ? (
                                            <><ArrowUpRight className="inline w-3 h-3" /> +{delta}</>
                                        ) : (
                                            <><ArrowDownRight className="inline w-3 h-3" /> {delta}</>
                                        )}
                                    </span>
                                    {" "}vs yesterday
                                </>
                            )}
                            {delta === 0 && " — unchanged from yesterday"}
                        </span>
                    </div>

                    <div className="sc-insight-item">
                        <BarChart3 />
                        <span>
                            {overallDelta > 0
                                ? <><strong>+{overallDelta}</strong> alerts over the week — investigate recent permission grants or configuration changes</>
                                : overallDelta < 0
                                    ? <><strong>{Math.abs(overallDelta)}</strong> fewer alerts over the week — remediation efforts are working</>
                                    : <>Alert count has been <strong>stable</strong> across the last 7 days</>
                            }
                        </span>
                    </div>

                    <div className="sc-insight-item">
                        <Zap />
                        <span>
                            Peak: <strong>{maxCount} alerts</strong> ({peakLabel}) · Lowest: <strong>{minCount} alerts</strong> ({lowLabel})
                        </span>
                    </div>

                    <div className="sc-insight-item">
                        <Shield />
                        <span>
                            7-day average: <strong>{avgCount} alerts</strong>
                            {latest > avgCount
                                ? <> — today is <strong>{latest - avgCount} above</strong> average</>
                                : latest < avgCount
                                    ? <> — today is <strong>{avgCount - latest} below</strong> average</>
                                    : <> — today is at average</>
                            }
                        </span>
                    </div>

                    {maxJump > 0 && (
                        <div className="sc-insight-item">
                            <TrendingUp />
                            <span>
                                Largest single-day spike: <strong>+{maxJump} alerts</strong> ({maxJumpDay}) — correlate with recent deployments
                            </span>
                        </div>
                    )}

                    {volatilityPct > 0 && (
                        <div className="sc-insight-item">
                            <Info />
                            <span>
                                Alert volatility: <strong>{volatilityPct}%</strong> — {volatilityPct > 30 ? "high variance indicates inconsistent security posture" : "steady trend indicates predictable access patterns"}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="sc-divider" />

            {/* Right: Alert Trend Chart */}
            <div className="sc-scores">
                <div className="sc-chart-wrapper">
                    <InteractiveAreaChart
                        values={chartValues}
                        labels={chartLabels}
                        color="hsl(var(--primary))"
                        gradientId="sc-trend-chart-gradient"
                        width={600}
                        height={240}
                    />
                </div>
            </div>
        </div>
    );
}
